/**
 * Normalize string: trim, collapse multiple spaces to one, init caps per word.
 * @param {string} str - Raw string
 * @returns {string} Trimmed, single spaces, first letter of each word uppercase
 */
export function toInitCaps(str) {
  if (str == null || typeof str !== 'string') return '';
  const trimmed = str.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format name to ensure it displays as "Firstname Lastname" with init caps and proper spaces.
 * Handles cases where name might be email, first name only, or already full name
 * @param {string} name - Name string (could be displayName, email, etc.)
 * @param {string} email - Email address as fallback
 * @returns {string} Formatted full name
 */
export function formatFullName(name, email = '') {
  if (!name && !email) return 'Unknown';

  let result = '';

  // If name is provided and looks like a full name (has space), use it
  if (name && name.includes(' ')) {
    result = name;
  } else if (name && !name.includes(' ')) {
    // Check if it's an email
    if (name.includes('@')) {
      const emailName = name.split('@')[0];
      result = formatEmailToName(emailName);
    } else if (email) {
      const emailName = email.split('@')[0];
      const emailParts = emailName.split('.');
      if (emailParts.length > 1) {
        result = `${name} ${emailParts[emailParts.length - 1].charAt(0).toUpperCase() + emailParts[emailParts.length - 1].slice(1)}`;
      } else {
        result = name;
      }
    } else {
      result = name;
    }
  } else if (email) {
    const emailName = email.split('@')[0];
    result = formatEmailToName(emailName);
  }

  if (result) return toInitCaps(result);
  return name ? toInitCaps(String(name)) : 'Unknown';
}

/**
 * Format email username to proper name format (init caps, single spaces)
 * @param {string} emailName - Email username (e.g., "rajesh.kumar")
 * @returns {string} Formatted name (e.g., "Rajesh Kumar")
 */
function formatEmailToName(emailName) {
  if (!emailName) return 'Unknown';
  const parts = String(emailName).trim().split(/[.\s]+/).filter(Boolean);
  const formattedParts = parts.map((part) =>
    part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );
  return formattedParts.join(' ');
}

/**
 * Get display name from user data, ensuring full name format
 * @param {object} userData - User data object with displayName and/or email
 * @returns {string} Formatted full name
 */
export function getUserDisplayName(userData) {
  if (!userData) return 'Unknown';

  return formatFullName(userData.displayName, userData.email);
}

/**
 * Get initials from a name for avatar fallback (e.g. "John Doe" -> "JD", "Alice" -> "A").
 * Max 2 characters; uses first letter of first two words, or first two letters of single word.
 * @param {string} name - Display name or email
 * @returns {string} 1–2 uppercase letters
 */
export function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const a = words[0].charAt(0);
    const b = words[1].charAt(0);
    return (a + b).toUpperCase();
  }
  const first = words[0];
  if (first.length >= 2) return first.slice(0, 2).toUpperCase();
  return first.charAt(0).toUpperCase();
}

/**
 * Deterministic background color for initials avatar (HSL from string hash).
 * @param {string} name - Same string used for getInitials (e.g. display name)
 * @returns {string} CSS color (e.g. "hsl(210, 55%, 45%)")
 */
export function getInitialsColor(name) {
  if (!name || typeof name !== 'string') return 'hsl(0, 0%, 45%)';
  let hash = 0;
  const str = name.trim() || '?';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash % 360);
  return `hsl(${h}, 55%, 45%)`;
}

